import { useEffect, useState } from "react";
import {
  CountriesDataType,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Switch, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { CountriesService } from "../../services/country.service";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import { FaPlus } from "react-icons/fa";

const Countries = () => {
  const [countries, setCountries] = useState<CountriesDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<CountriesDataType | null>(
    null
  );
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState("AZ");

  useEffect(() => {
    setLoading(true);
    CountriesService.getAllCountries()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: CountriesDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setCountries(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);
  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await CountriesService.getAllCountries();
    setCountries(
      updatedData?.data?.data?.map(
        (item: CountriesDataType, index: number) => ({
          ...item,
          key: index + 1,
        })
      )
    );
  };
  const {
    handleSubmit,
    handleChange,
    values,
    resetForm,
    setFieldValue,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      name: "",
      code: "",
      is_visible: false,
      phone_code: null,
      translations: [] as { lang_code: string; text: string }[],
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Ölkə adı tələb olunur"),
      code: Yup.string().required("Ölkə kodu tələb olunur"),
    }),
    onSubmit: async (values) => {
      const azTranslation = {
        lang_code: "AZ",
        text: values.name,
      };
      const updatedTranslations = [
        azTranslation,
        ...values.translations.filter((t) => t.lang_code !== "AZ"),
      ];
      const finalValues = {
        ...values,
        translations: updatedTranslations,
      };
      if (isEditing && editingRecord) {
        const isEqual =
          editingRecord.name === values.name &&
          editingRecord.code === values.code &&
          editingRecord.phone_code === values.phone_code &&
          editingRecord.is_visible === values.is_visible &&
          editingRecord.translations === values.translations;
        if (isEqual) {
          message.info("Dəyişiklik edilmədi.");
        } else {
          const response = await CountriesService.updateCountry({
            ...finalValues,
            id: editingRecord.id,
          });
          if (response.status === 200) {
            message.success("Ölkə uğurla yeniləndi!");
            await handleAfterSubmit();
          } else {
            message.error(
              response.errorMessage || "Ölkəni yeniləmək alınmadı."
            );
          }
        }
      } else {
        const response = await CountriesService.addCountry(finalValues);
        if (response.status === 200) {
          message.success("Ölkə uğurla əlavə edildi!");
          await handleAfterSubmit();
        } else {
          message.error(response.errorMessage || "Ölkə əlavə etmək alınmadı.");
        }
      }
      setLoading(false);
    },
  });
  const handleTranslationChange = (value: string) => {
    if (langCode === "AZ") {
      setFieldValue("name", value);
    } else {
      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.lang_code === langCode
      );

      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          text: value,
        };
      } else {
        updatedTranslations.push({ lang_code: langCode, text: value });
      }

      setFieldValue("translations", updatedTranslations);
    }
  };

  const handleDeleteClick = async (record: CountriesDataType) => {
    try {
      await CountriesService.deleteCountry(record.id.toString());
      setCountries((prevData) =>
        prevData.filter((item) => item.id !== record.id)
      );
      message.success("Ölkə uğurla silindi!");
    } catch (error) {
      message.error("Ölkəni silmək alınmadı.");
    }
  };
  const handleSwitchChange = async (checked: boolean, recordId: number) => {
    CountriesService.updateCountry({
      id: recordId,
      is_visible: checked,
    });
    if (checked) {
      message.success("Ölkə artıq saytda görünür.");
    } else {
      message.info("Ölkə artıq saytda görünmür.");
    }
    setCountries((prevData) =>
      prevData.map((item) =>
        item.id === recordId ? { ...item, is_visible: checked } : item
      )
    );
  };
  const columns: TableProps<CountriesDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.key - b.key,
    },
    {
      title: "Ölkə adı",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Ölkə kodu",
      dataIndex: "code",
    },
    {
      title: "Nömrə kodu",
      dataIndex: "phone_code",
    },
    {
      title: "Saytda görünürlük",
      dataIndex: "is_visible",
      render: (_, record) => (
        <div className="switch_input">
          <Switch
            checked={record.is_visible}
            onChange={(checked) => handleSwitchChange(checked, record.id)}
          />
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu ölkəni siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setLangCode("AZ");
  };
  const handleEditClick = (record: CountriesDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("name", record.name);
    setFieldValue("code", record.code);
    setFieldValue("phone_code", record.phone_code);
    setFieldValue("is_visible", record.is_visible);
    setFieldValue("translations", record.translations || []);
    setLangCode("AZ");
  };

  return (
    <div id="pages">
      <Helmet>
        <title>1412 | Ölkələr</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Ölkələr</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<CountriesDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={countries}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={isEditing ? "Ölkəni redaktə edin" : "Yeni Ölkə əlavə edin"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <ChangeLanguage
            langCode={langCode}
            setLangCode={setLangCode}
            values={values}
            setFieldValue={setFieldValue}
            langs={langs}
          />
          <div className="form_input">
            <label className="label" htmlFor="name">
              Ölkə adı {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="name"
                id="name"
                placeholder="Ölkə adı"
                value={
                  langCode === "AZ"
                    ? values.name
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.text || ""
                }
                onChange={(e) => handleTranslationChange(e.target.value)}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.name} touched={touched.name} />
          </div>
          <div className="form_input">
            <label className="label" htmlFor="Code">
              Kod
            </label>
            <div className="input_wrapper">
              <input
                name="code"
                id="code"
                placeholder="Kod"
                value={values.code || ""}
                onChange={handleChange}
              />
              <span
                className="focus_border
"
              ></span>
            </div>
            <ErrorMessage error={errors.code} touched={touched.code} />
          </div>
          <div className="form_input">
            <label className="label" htmlFor="Phone code">
              Nömrə kodu
            </label>
            <div className="input_wrapper">
              <input
                name="phone_code"
                id="phone_code"
                placeholder="Nömrə kodu"
                value={values.phone_code || ""}
                onChange={handleChange}
              />
              <span
                className="focus_border
"
              ></span>
            </div>
          </div>
          <div className="form_input">
            <label className="active_label" htmlFor="">
              Ölkənin saytda görünməsi
            </label>
            <Switch
              checked={values.is_visible}
              onChange={(checked) => setFieldValue("is_visible", checked)}
            />
          </div>
          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? " Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Countries;
